export enum PaymentStageName {
  DEPOSIT = 'DEPOSIT',
  BASE = 'BASE',
  FRAME = 'FRAME',
  LOCK_UP = 'LOCK_UP',
  FIXING = 'FIXING',
  PRACTICAL_COMPLETION = 'PRACTICAL_COMPLETION',
  OTHER = 'OTHER',
  UNKNOWN = 'UNKNOWN',
}

export enum PaymentStatus {
  SCHEDULED = 'SCHEDULED',
  LINKED = 'LINKED',
  BREACHED = 'BREACHED',
  SECURED = 'SECURED',
  COMPLETED = 'COMPLETED',
  SUBMITTED = 'SUBMITTED',
}

export enum ProgressPaymentTag {
  DEPOSIT = 'DEPOSIT',
  COS_DEPOSIT = 'COS_DEPOSIT',
  MARKETING = 'MARKETING',
  COMMISSION = 'COMMISSION',
}

export enum PaymentTermsType {
  DEFAULT = 'DEFAULT',
  EOM = 'EOM',
}

export enum PaymentSecuredStatus {
  SECURED = 'SECURED',
  PARTIALLY_SECURED = 'PARTIALLY_SECURED',
  NOT_SECURED = 'NOT_SECURED',
  TRANSFERRED = 'TRANSFERRED',
}

export enum DefaultPaymentActionType {
  CLAIM = 'CLAIM',
  UPDATE = 'UPDATE',
  LINK = 'LINK',
  MARK_AS_PAID = 'MARK_AS_PAID',
  RELEASE = 'RELEASE',
  SECURE = 'SECURE',
  EDIT = 'EDIT',
  REQUEST_ASSESSMENT = 'REQUEST_ASSESSMENT',
  CLAIM_ASSESSMENT = 'CLAIM_ASSESSMENT',
  RECALL = 'RECALL',
  UPDATE_CLAIM_AMOUNT = 'UPDATE_CLAIM_AMOUNT',
}

export enum PaymentType {
  STANDARD = 'STANDARD',
  CUSTOM = 'CUSTOM',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum PaymentScheduleType {
  DOM = 'DOM', // day of month
  EOM = 'EOM', // end of month
}

export enum SecurityType {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  PERCENTAGE = 'PERCENTAGE',
}

export interface LinkablePayment {
  id: string;
  amount: number;
  stage: PaymentStageName;
  percentOfContractSum: string;
  description?: string;
  customName?: string;
  linkStatus: {
    linkable: boolean;
    statusCode: PaymentLinkStatus;
    errMessage: string;
  };
}

export enum PaymentLinkStatus {
  PAYMENT_ALREADY_CLAIMED = 'PAYMENT_ALREADY_CLAIMED',
  PAYMENT_NOT_SCHEDULED = 'PAYMENT_NOT_SCHEDULED',
  PAYMENT_ALREADY_LINKED = 'PAYMENT_ALREADY_LINKED',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  PAYMENT_FULLY_SECURED = 'PAYMENT_FULLY_SECURED',
  LINKABLE = '',
}

export const paymentLinkStatusText = {
  [PaymentLinkStatus.PAYMENT_ALREADY_CLAIMED]: 'Already claimed',
  [PaymentLinkStatus.PAYMENT_NOT_SCHEDULED]: 'Not scheduled',
  [PaymentLinkStatus.PAYMENT_ALREADY_LINKED]: 'Already linked',
  [PaymentLinkStatus.INSUFFICIENT_FUNDS]: 'Insufficient funds',
  [PaymentLinkStatus.PAYMENT_FULLY_SECURED]: 'Already fully secured',
  [PaymentLinkStatus.LINKABLE]: '',
};

export const paymentLinkStatusTooltip = {
  [PaymentLinkStatus.PAYMENT_ALREADY_CLAIMED]:
    'A progress payment cannot be linked if already claimed.',
  [PaymentLinkStatus.PAYMENT_NOT_SCHEDULED]: 'A progress payment cannot be linked if not scheduled',
  [PaymentLinkStatus.PAYMENT_ALREADY_LINKED]:
    'A progress payment cannot be linked if already linked.',
  [PaymentLinkStatus.INSUFFICIENT_FUNDS]:
    'Insufficient funds in this progress payment to pay the linked claim.',
  [PaymentLinkStatus.PAYMENT_FULLY_SECURED]:
    'A progress payment cannot be linked if already fully secured.',
  [PaymentLinkStatus.LINKABLE]: '',
};

/**
 * How far a card payment got, as payment-service reports it on
 * `GET /card-payments/{paymentRef}/status`.
 *
 * `FAILED` is a technical failure at the acquirer rather than a refusal by the card issuer —
 * payment-service keeps the two apart so a failure is not dressed up as a decline. `UNKNOWN` is
 * its fallback for a transaction status it does not recognise.
 */
export enum PaymentAuthorizedStatus {
  INITIATED = 'INITIATED',
  PENDING = 'PENDING',
  AUTHORIZED = 'AUTHORIZED',
  DECLINED = 'DECLINED',
  FAILED = 'FAILED',
  ABANDONED = 'ABANDONED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Why a card payment reached its status.
 *
 * Sent whenever payment-service recognises the reason the acquirer gave, and on every decline —
 * an unrecognised decline arrives as `UNKNOWN` rather than nothing, so a decline always carries
 * a reason. The `statusReasonDescription` alongside it is the acquirer's own wording and is
 * never shown to anyone; our copy comes from `getCardPaymentFailureMessage`.
 */
export enum CardPaymentStatusReasonCode {
  WITHDRAWAL_LIMIT_EXCEEDED = 'WITHDRAWAL_LIMIT_EXCEEDED',
  UNKNOWN = 'UNKNOWN',
}
